import { DonutChart, IChartDataPoint } from '@fluentui/react-charting';
import { IPFontIcon, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { useWidgetDrillDownDisabled } from 'components/Dashboard/WidgetDrillDownContext';
import HrefLink from 'components/Links/HrefLink';
import { globalStackStylesHeight100, globalStackTokensGapSmall } from 'globalStyles';
import randomColor from 'randomcolor';
import { useHistory } from 'react-router-dom';
import { navigateToExternalUrl } from 'utils/url';

export interface IPieChartDataPoint {
  name: string;
  value: number;
  color?: string;
  link?: string;
  linkExternal?: boolean;
  hideValueInLegend?: boolean;
}

export interface IPieChartStdProps {
  width?: number;
  height?: number;
  data: IPieChartDataPoint[];
  label?: string;
  hideTooltip?: boolean;
  hideLegend?: boolean;
  customLegend?: boolean;
}

export const FluentUIPieChart = (props: IPieChartStdProps) => {
  const history = useHistory();
  const drillDownDisabled = useWidgetDrillDownDisabled();

  const getChartData = (data: IPieChartDataPoint[]): IChartDataPoint[] => {
    return data
      .map((item) => {
        return {
          color: item.color ?? randomColor(),
          data: item.value,
          legend: item.name,
          onClick: drillDownDisabled
            ? undefined
            : () => {
                if (item.link) {
                  if (item.linkExternal) {
                    navigateToExternalUrl(item.link, undefined, '');
                  } else {
                    history.push(item.link);
                  }
                }
              },
        };
      })
      .filter((d) => (d.data ?? 0) > 0);
  };

  return (
    <IPStack horizontal styles={globalStackStylesHeight100} tokens={{ childrenGap: 0 }} verticalAlign="center">
      <IPStack.Item grow>
        <DonutChart
          data={{ chartTitle: props.label, chartData: getChartData(props.data) }}
          innerRadius={props.height ? props.height / 3.5 : 60}
          hideLegend={props.hideLegend || props.customLegend}
          valueInsideDonut={props.label}
          width={props.width && props.hideLegend ? props.width / 2 : props.width}
          height={props.height}
          hideTooltip={props.hideTooltip}
          enabledLegendsWrapLines={true}
          roundCorners={true}
        />
      </IPStack.Item>
      {!props.hideLegend && props.customLegend && (
        <IPStack.Item>
          <IPStack tokens={globalStackTokensGapSmall}>
            {props.data.map((item) => (
              <SingleLegend {...item} key={item.name} />
            ))}
          </IPStack>
        </IPStack.Item>
      )}
    </IPStack>
  );
};

export const SingleLegend = (props: IPieChartDataPoint) => {
  let text;
  let itemToDisplay;

  if (!props.hideValueInLegend) {
    text = `${props.name} : ${props.value}`;
  } else {
    text = props.name;
  }

  if (props.link) {
    itemToDisplay = (
      <HrefLink href={props.link} external={props.linkExternal} underline>
        {text}
      </HrefLink>
    );
  } else {
    itemToDisplay = <IPText>{text}</IPText>;
  }

  return (
    <IPStack horizontal tokens={globalStackTokensGapSmall} verticalAlign="center">
      <IPFontIcon iconName="CircleShapeSolid" style={{ color: props.color }} />
      {itemToDisplay}
    </IPStack>
  );
};
